import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import { observer } from "mobx-react";
import dataStore from "../../Store/dataStore";
import * as salesOrderService from "../../Services/Http/SalesOrderService";
import { Customer } from "../../Models/SalesOrder";
import { loadSalesOrderDraft, saveSalesOrderDraft, todayIso, SalesDocMode, salesDocText } from "../../Helpers/SalesOrderDraft";

//Order, step 1: choose the customer, or create one. Then the order screen opens for scanning items.
//Customers come from eSuite's GET /cust; the API caches the list and filters it, so any part of a name, code,
//phone or email finds them.

interface Props {
  //"quote" builds a sales quote instead of an order
  mode?: SalesDocMode;
}

const errorText = (error: any, fallback: string = "Something went wrong"): string => {
  if (typeof error === "string" && error) {
    return error;
  }
  return (error && typeof error.message === "string" && error.message) || fallback;
};

const customerContact = (c: Customer): string =>
  [c.accountPhone1 || c.accountMobile, c.accountEmail].filter((x) => !!x).join("  ·  ");

const SalesOrderCustomerPage = observer((props: Props) => {
  const mode: SalesDocMode = props.mode || "order";
  const text = salesDocText(mode);
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null);
  //an order / quote already started on this device
  const [draftCustomer, setDraftCustomer] = useState<Customer | null>(null);
  const [draftLineCount, setDraftLineCount] = useState<number>(0);

  const [newOpen, setNewOpen] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>("");
  const [newPhone, setNewPhone] = useState<string>("");
  const [newEmail, setNewEmail] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);

  useLayoutEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const draft = loadSalesOrderDraft(mode);
    if (draft && draft.customer) {
      setDraftCustomer(draft.customer);
      setDraftLineCount((draft.lines || []).length);
    }
  }, [mode]);

  //search as they type, once they stop for a moment
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      salesOrderService
        .searchCustomersAPI(search.trim(), dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((list) => {
          if (!cancelled) {
            setCustomers(list || []);
          }
        })
        .catch((error: any) => {
          if (!cancelled) {
            toast.error(errorText(error, "Customer search failed"), { autoClose: 3000 });
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const startOrder = (customer: Customer) => {
    const existing = loadSalesOrderDraft(mode);
    if (existing && existing.customer && (existing.lines || []).length > 0 && existing.customer.accountRef !== customer.accountRef) {
      if (!window.confirm(`Discard the ${text.noun} already started for ${existing.customer.accountName || existing.customer.accountRef}?`)) {
        return;
      }
    }
    if (existing && existing.customer && existing.customer.accountRef === customer.accountRef) {
      //same customer - carry on with what was already scanned
      saveSalesOrderDraft(mode, { ...existing, customer });
    } else {
      saveSalesOrderDraft(mode, { customer, comment: "", dueDate: todayIso(), lines: [] });
    }
    navigate(text.docRoute);
  };

  const onCreateCustomer = () => {
    if (!newName.trim()) {
      toast.error("Enter the customer's name", { autoClose: 2000 });
      return;
    }
    setCreating(true);
    salesOrderService
      .createCustomerAPI({ name: newName.trim(), phone: newPhone.trim(), email: newEmail.trim() }, dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((customer) => {
        toast.success(`Created customer ${customer.accountRef}`, { autoClose: 1500 });
        setNewOpen(false);
        startOrder(customer);
      })
      .catch((error: any) => {
        toast.error(errorText(error, "Creating the customer failed"), { autoClose: false });
      })
      .finally(() => setCreating(false));
  };

  return (
    <>
      <div className="container-limited-width">
        {draftCustomer && (
          <div className="row mt-2">
            <div className="col-12">
              <Button variant="warning" className="w-100" onClick={() => navigate(text.docRoute)}>
                Continue {text.noun} for {draftCustomer.accountName || draftCustomer.accountRef}
                {draftLineCount > 0 ? ` (${draftLineCount} item${draftLineCount === 1 ? "" : "s"})` : ""}
              </Button>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="customerSearch"
                type="text"
                className="form-control search-box"
                placeholder="Search customer name, code, phone or email"
                autoCapitalize="none"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                ref={searchRef}
              />
              <div className="input-group-text search-btn" title="Clear" onClick={() => setSearch("")}>
                <i className="fa fa-times-circle-o"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Button
              variant="primary"
              className="w-100"
              onClick={() => {
                setNewName(search.trim());
                setNewPhone("");
                setNewEmail("");
                setNewOpen(true);
              }}
            >
              <i className="fa fa-user-plus me-2" aria-hidden="true"></i>NEW CUSTOMER
            </Button>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2">
            {loading && customers.length === 0 && <div className="text-muted text-center my-3">Searching...</div>}
            {!loading && customers.length === 0 && (
              <div className="text-muted text-center my-3">
                {search.trim() ? "No customers match" : "No customers found"}
              </div>
            )}
            <ul className="list-group orders-list">
              {customers.map((c) => (
                <li key={`${c.companyNo}_${c.accountNo}`} className="list-group-item d-flex justify-content-between align-items-center" onClick={() => startOrder(c)}>
                  <div className="d-flex flex-column">
                    <div className="fw-bold">{c.accountName || `${c.accountFirstName || ""} ${c.accountLastName || ""}`.trim() || c.accountRef}</div>
                    {customerContact(c) && <div className="text-muted small">{customerContact(c)}</div>}
                  </div>
                  <span className="text-muted small text-nowrap ms-2">{c.accountRef}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Modal show={newOpen} onHide={() => setNewOpen(false)} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <label className="form-check-label">Name</label>
            <input type="text" className="form-control" maxLength={60} value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
          </div>
          <div className="mb-2">
            <label className="form-check-label">Phone</label>
            <input type="tel" className="form-control" maxLength={30} value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
          </div>
          <div>
            <label className="form-check-label">Email</label>
            <input type="email" className="form-control" maxLength={100} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={onCreateCustomer} disabled={creating}>
            {creating ? "Creating..." : "Create"}
          </Button>
          <Button variant="secondary" onClick={() => setNewOpen(false)} disabled={creating}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default SalesOrderCustomerPage;
